import type { QualificationAttribute } from "../../types/JobAttribute";
import type { QualificationCollectionInitialData } from "../../types/ProviderParams";

import { getSubtitleProps } from "./util";
import { useQualificationSections } from "./util/useQualificationSections";
import { useI18n, useLocale } from "../../../../../../../shared/I18n";
import { useTareData } from "../../../../../../../shared/native-modules/tare/TareDataContext";
import { useViewjobProctorAllocation } from "../../../../../../proctor/__generated__/ViewjobProctorContext";
import { MobVjMatchOverviewTst } from "../../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobOneGraphModel } from "../../../../../context/ViewJobDataContext";
import { useFullScreenVJActive } from "../../../../../hooks/useFullScreenVJActive";
import { useMatchOverviewResumeGate } from "../../../../../hooks/useMatchOverviewResumeGate";
import { BASE_PROVIDER_ID } from "../../constants/common";
import ViewType from "../../constants/ViewType";
import { JMUIContextProviders } from "../../context/JMUIContextProviders";
import { useFeatureFlags } from "../../context/useFeatureFlags";
import { useLogging } from "../../context/useLogging";
import { getInsightsModel } from "../../models/InsightsComponentModel";
import { AttributeCollection } from "../common/AttributeCollection";
import { GatedMatchOverview } from "../GatedMatchOverview/GatedMatchOverview";
import { MatchOverviewQualificationCollection } from "../MatchOverviewQualificationCollection";

import { AvroOptional } from "@indeed/signals-logger";
import React, { useEffect } from "react";

export type QualificationCollectionProps = Pick<
  QualificationCollectionInitialData,
  "attributes" | "viewType"
>;

export const QualificationCollection: React.FC<QualificationCollectionProps> = ({
  attributes,
  viewType,
}) => {
  const logger = useLogging();
  const gt = useI18n();
  const isFullScreenVJActive = useFullScreenVJActive();

  const { onClearError, errors, sections, sentimentCounts, missingQualifications } =
    useQualificationSections({
      attributes,
      viewType,
    });

  useEffect(() => {
    logger.logShowedProfileInsights();
  });

  return (
    <AttributeCollection
      missingQualifications={missingQualifications}
      sections={sections}
      title={isFullScreenVJActive ? gt.gettext("Match overview") : gt.gettext("Profile insights")}
      subtitleProps={getSubtitleProps(gt, viewType)}
      hasDivider
      errors={errors}
      onClearError={onClearError}
      viewType={viewType}
      sentimentCounts={sentimentCounts}
      sx={{ marginVertical: 24 }}
    />
  );
};

export const QualificationCollectionWithContext: React.FC = () => {
  return (
    <JMUIContextProviders providerId={BASE_PROVIDER_ID}>
      <QualificationCollectionRenderer />
    </JMUIContextProviders>
  );
};

export const QualificationCollectionRenderer: React.FC = () => {
  const vjModel = useViewjobOneGraphModel();
  const tareData = useTareData();
  const { country, language } = useLocale();
  const featureFlags = useFeatureFlags();
  const logger = useLogging();

  // Check if match overview test is active (grp1 or grp2)
  const matchOverviewTestValue = useViewjobProctorAllocation(MobVjMatchOverviewTst).value;
  const isProfileInsightsActive = matchOverviewTestValue < 1;
  const isMatchOverviewActive = matchOverviewTestValue === 2;
  // Check if resume-gate UI should show in place of Match Overview
  const isResumeGated = useMatchOverviewResumeGate();

  let shouldRender = false;
  let attributes: QualificationAttribute[] = [];
  let viewType: ViewType = ViewType.UNKNOWN;
  let oneGraphMatchComparison:
    | component.oneGraphMatchComparison.OneGraphMatchComparison
    | undefined;
  let isJobExpired: boolean;

  if (vjModel) {
    oneGraphMatchComparison = vjModel.jobSeeker.matchComparison;

    isJobExpired = vjModel.job.isExpired ?? false;
    const jobSeekerSuidsToFactIds = vjModel.jobSeeker.preferences?.jobSeekerSuidsToFactIds || [];
    const isProfileInsightsBlocked = vjModel.jobSeeker.profile.isProfileInsightsBlocked;
    const loggedIn = tareData.accountStatus?.isLoggedIn ?? false;

    // Check if we should continue with rendering
    if (oneGraphMatchComparison && !isJobExpired && !isProfileInsightsBlocked) {
      const insightsModel = getInsightsModel(
        featureFlags,
        loggedIn,
        oneGraphMatchComparison,
        jobSeekerSuidsToFactIds,
      );

      if (insightsModel.isEnabled) {
        shouldRender = true;
        attributes = insightsModel.attributes;
        viewType = insightsModel.viewType;
      }
    }
  }

  useEffect(() => {
    logger.logProviderRequest(
      {
        jobKey: vjModel?.job.jobKey || "",
        candEvalModel: AvroOptional.string(oneGraphMatchComparison?.encouragementToApply?.strategy),
        candEvalRawScore: AvroOptional.double(oneGraphMatchComparison?.encouragementToApply?.score),
        candEvalTrafficLightSignal: AvroOptional.string(
          oneGraphMatchComparison?.encouragementToApply?.trafficLight,
        ),
        isJobExpired: AvroOptional.int(isJobExpired ? 1 : 0),
        jobCountry: vjModel?.job.jobCountry || "",
        jobLanguage: vjModel?.job.jobLanguage || "",
        occupationIds: vjModel?.job.jobOccupations?.join(",") || "",
        requestCountry: country || "",
        requestLanguage: language || "",
        resumeType: vjModel?.jobSeeker.profile.resumeType ?? "NO_RESUME",
        shouldShowInsightsComponent: shouldRender ? 1 : 0,
        taxonomyEntitiesCount: oneGraphMatchComparison?.attributeComparisons?.length || 0,
      },
      attributes,
    );
  });

  // Render match overview variant when test is active (grp1 or grp2)
  if (shouldRender && isMatchOverviewActive) {
    // Gate the requirements list behind a resume for seekers with none on file (MJP-880).
    // CTA handlers are wired up in RN-4782 (build) / RN-4783 (upload); no-ops for now.
    if (isResumeGated) {
      return <GatedMatchOverview onBuildPress={() => {}} onUploadPress={() => {}} />;
    }

    return <MatchOverviewQualificationCollection attributes={attributes} viewType={viewType} />;
  }

  // Render standard qualification collection
  return shouldRender && isProfileInsightsActive ? (
    <QualificationCollection attributes={attributes} viewType={viewType} />
  ) : null;
};
